import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

import { OrderStatKind, OrderStatResult } from './adminOrderStat.types';

//* 프로젝트 표준(커뮤니티 xlsx + file-saver). 스타일 플러그인은 쓰지 않고
//* 너비/고정/필터/숫자포맷으로만 가독성을 낸다.

const charWidth = (value: unknown): number => {
  const text = value === null || value === undefined ? '' : String(value);
  //* 한글은 영문보다 넓다 — 대략 2배로 잡는다
  // eslint-disable-next-line no-control-regex
  return text.split('').reduce((sum, ch) => sum + (/[^\x00-\xff]/.test(ch) ? 2 : 1), 0);
};

const autoWidth = (rows: (string | number | null)[][]): XLSX.ColInfo[] => {
  const widths: number[] = [];
  rows.forEach((row) => {
    row.forEach((cell, index) => {
      widths[index] = Math.max(widths[index] ?? 0, charWidth(cell));
    });
  });
  return widths.map((w) => ({ wch: Math.min(Math.max(w + 2, 6), 40) }));
};

/** 헤더 고정 — 커뮤니티 xlsx는 !views까지 있어야 실제로 고정된다 */
const freezeHeader = (ws: XLSX.WorkSheet): void => {
  ws['!freeze'] = { xSplit: 0, ySplit: 1 };
  ws['!views'] = [{ state: 'frozen', ySplit: 1 }] as XLSX.WorkSheet['!views'];
};

const round = (value: number) => Math.round(value);
const div1 = (a: number, b: number): number | null =>
  b > 0 ? Math.round((a / b) * 10) / 10 : null;

export const exportOrderStatExcel = (
  kind: OrderStatKind,
  result: OrderStatResult,
  period: { from: string; to: string },
): void => {
  const isGift = kind === 'gift';
  const kindLabel = isGift ? '답례품' : '화환';

  const header = [
    '상조',
    '주문',
    '금액',
    '건당금액',
    '수량',
    '상가',
    '상가당 주문',
    ...(isGift ? ['전체취소'] : []),
  ];

  const rowOf = (row: {
    brandName?: string;
    orderCount: number;
    amount: number;
    quantity: number;
    feventCount: number;
    canceledCount: number;
  }) => [
    row.brandName ?? '합계',
    row.orderCount,
    round(row.amount),
    row.orderCount > 0 ? round(row.amount / row.orderCount) : null,
    row.quantity,
    row.feventCount,
    div1(row.orderCount, row.feventCount),
    ...(isGift ? [row.canceledCount] : []),
  ];

  const rows: (string | number | null)[][] = [
    header,
    ...result.brands.map((brand) => rowOf(brand)),
    rowOf(result.totals),
  ];

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = autoWidth(rows);
  freezeHeader(ws);
  //* 합계행은 필터 범위에서 제외
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: rows.length - 2, c: header.length - 1 },
    }),
  };
  XLSX.utils.book_append_sheet(wb, ws, '상조별');

  const dayRows: (string | number | null)[][] = [
    ['일자', '주문', '금액'],
    ...result.days.map((day) => [day.date, day.orderCount, round(day.amount)]),
  ];
  const dayWs = XLSX.utils.aoa_to_sheet(dayRows);
  dayWs['!cols'] = autoWidth(dayRows);
  freezeHeader(dayWs);
  XLSX.utils.book_append_sheet(wb, dayWs, '일자별');

  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(
    new Blob([out], { type: 'application/octet-stream' }),
    `${kindLabel}통계_${period.from}_${period.to}.xlsx`,
  );
};
